import type {
  AdminUser,
  AdminUsersListResponse,
  InvestigatorAccessRequest,
  PendingDirectoryUser,
  SignupAttempt,
} from '../api/types';

export const DIRECTORY_STATUSES = [
  'Signed in',
  'Pending first login',
  'Pending sign-up',
  'Needs attention',
  'Rejected',
  'Reviewed',
] as const;

export interface DirectoryRow {
  key: string;
  name: string;
  email: string | null;
  tenantId: string | null;
  tenant: string | null;
  teams: { id: string; name: string }[];
  roles: string[];
  lastLogin: string | null;
  status: (typeof DIRECTORY_STATUSES)[number];
  detail?: string;
  attemptedAt?: string;
  user?: AdminUser;
  attempt?: SignupAttempt;
  /** A person invited to a team who has not signed up yet. */
  pending?: PendingDirectoryUser;
  /** Pending requests to work for a PI, filed in this row's tenant. */
  accessRequests: InvestigatorAccessRequest[];
  /** A registered user marked internal, so Product health leaves them out. */
  internal: boolean;
}

export function directoryRows(
  data: AdminUsersListResponse | undefined,
  attempts: SignupAttempt[]
): DirectoryRow[] {
  const users = (data?.users ?? []).flatMap(user => {
    const affiliations = user.affiliations?.length
      ? user.affiliations
      : [
          {
            tenant_id: user.tenant_id,
            tenant_name: user.tenant_name,
            role_names: user.role_names,
            teams: [],
          },
        ];
    return affiliations.map(affiliation => ({
      key: `user:${user.id}:${affiliation.tenant_id ?? ''}`,
      name: user.display_name || user.username,
      email: user.email,
      tenantId: affiliation.tenant_id,
      tenant: affiliation.tenant_name,
      teams: affiliation.teams,
      roles: [
        ...new Set([...affiliation.role_names, ...(user.is_super_admin ? ['super_admin'] : [])]),
      ],
      lastLogin: user.last_login,
      status: user.last_login ? ('Signed in' as const) : ('Pending first login' as const),
      user,
      accessRequests: (user.pending_access_requests ?? []).filter(
        request => request.tenant_id === affiliation.tenant_id
      ),
      internal: user.is_internal === true,
    }));
  });
  const pending = (data?.pending_users ?? []).map(person => ({
    key: `pending:${person.id}`,
    name: 'Awaiting sign-up',
    email: person.email,
    tenantId: person.tenant_id,
    tenant: person.tenant_name,
    teams: person.teams,
    roles: person.role_names,
    lastLogin: null,
    status: person.failure_reason ? ('Needs attention' as const) : ('Pending sign-up' as const),
    detail: person.failure_reason ?? undefined,
    pending: person,
    accessRequests: [],
    internal: false,
  }));
  const rejected = attempts.map(attempt => ({
    key: `attempt:${attempt.id}`,
    name: 'Sign-up attempt',
    email: attempt.email,
    tenantId: null,
    tenant: null,
    teams: [],
    roles: [],
    lastLogin: null,
    status: attempt.reviewed_at ? ('Reviewed' as const) : ('Rejected' as const),
    detail: attempt.reason,
    attemptedAt: attempt.created_at,
    attempt,
    accessRequests: [],
    internal: false,
  }));
  return [...users, ...pending, ...rejected];
}

export interface DirectoryFilters {
  search: string;
  tenant: string;
  team: string;
  role: string;
  status: string;
  /** 'pending' keeps rows with a pending PI access request. */
  requests: string;
  /** 'internal' keeps internal users, 'external' everyone else. */
  internal: string;
}

export const EMPTY_FILTERS: DirectoryFilters = {
  search: '',
  tenant: '',
  team: '',
  role: '',
  status: '',
  requests: '',
  internal: '',
};

/** Whether this Rudi release reports which users are internal (Rudi ADR 0086). */
export function reportsInternalUsers(data: AdminUsersListResponse | undefined): boolean {
  return (data?.users ?? []).some(user => user.is_internal !== undefined);
}

/** Whether this Rudi release reports PI access requests in the user list. */
export function reportsAccessRequests(data: AdminUsersListResponse | undefined): boolean {
  return (data?.users ?? []).some(user => user.pending_access_requests !== undefined);
}

export function filterDirectory(rows: DirectoryRow[], filters: DirectoryFilters): DirectoryRow[] {
  const words = filters.search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter(row => {
    const haystack = [
      row.name,
      row.user?.username,
      row.email,
      row.tenant,
      ...row.teams.map(team => team.name),
      ...row.roles,
      row.status,
      ...row.accessRequests.map(request => request.pi_name ?? request.pi_slug),
    ]
      .filter(Boolean)
      .join(' ')
      .replaceAll('_', ' ')
      .toLocaleLowerCase();
    return (
      words.every(word => haystack.includes(word.replaceAll('_', ' '))) &&
      (!filters.tenant || (row.tenantId ?? 'unassigned') === filters.tenant) &&
      (!filters.team ||
        (filters.team === 'unassigned'
          ? row.teams.length === 0
          : row.teams.some(team => team.id === filters.team))) &&
      (!filters.role ||
        (filters.role === 'unassigned'
          ? row.roles.length === 0
          : row.roles.includes(filters.role))) &&
      (!filters.status || row.status === filters.status) &&
      (!filters.requests || row.accessRequests.length > 0) &&
      (!filters.internal || row.internal === (filters.internal === 'internal'))
    );
  });
}
